import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  AdBeat, AdBeatKind, AdBeatSeconds, AdBriefState, AdInputs, AdMotion, AdOverlay, AdPlacement, AdPlan, AdStill, AdStillAspect,
} from "./types";
import {
  requestAdRender, requestAdReplan, requestAdDiscard,
  useAdPlanPatch, useAdInputsPatch, useAdCommit,
} from "./studioBus";
import { usePayJob } from "@/components/pay/useResumeJob";
import SiteMarkSwitch from "@/components/pay/SiteMarkSwitch";

// AdBriefCard — the approval gate for a commercial, built on the same
// brief-node blueprint as StoryBriefCard (376 wide, 20 padding, 16 gap).
//
// Everything on it is something the worker really consumes
// (docs/ads-tool-contract.md §3): beat kind, seconds, visual prompt, motion,
// which customer photo is composited, narration, overlay text, the script, the
// end card, still concepts, and the voice / music / captions switches.
//
// The product is ALWAYS the customer's own pixels. The card never offers a
// way to "generate" a product: a product beat points at one of the uploaded
// photos, and the prompt only describes the scene around it.
//
// Nothing renders and nothing is charged until Generate is pressed.

const C = {
  cardBg: "#0e141d",
  border: "#223046",
  fill: "rgba(255,255,255,.06)",
  textPrimary: "#e9eef5",
  textSecondary: "#b6c2d4",
  textTertiary: "rgba(220,225,240,.7)",
  green: "#22c55e",
  gold: "#e3b341",
  muted: "#7c8aa0",
  red: "#f87171",
};

const KINDS: AdBeatKind[] = ["product_hero", "product_detail", "lifestyle", "avatar_talk", "avatar_product", "text_card", "end_card"];
const MOTIONS: AdMotion[] = ["slow push in", "pan left", "pan right", "pull back", "orbit"];
const PLACEMENTS: AdPlacement[] = ["center", "left", "right", "bottom"];
const STILL_ASPECTS: AdStillAspect[] = ["1:1", "4:5", "9:16", "16:9"];
const SECONDS: AdBeatSeconds[] = [2.5, 5];
const PRODUCT_KINDS: AdBeatKind[] = ["product_hero", "product_detail", "avatar_product"];
const AVATAR_KINDS: AdBeatKind[] = ["avatar_talk", "avatar_product"];
const EMPTY_OVERLAY: AdOverlay = { headline: "", subline: "", price: "", cta: "" };

const motionKey = (m: AdMotion) => m.replace(/\s+/g, "_");

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ color: C.textPrimary, fontWeight: 700, fontSize: 14, letterSpacing: ".094px", lineHeight: "150%", marginBottom: -4 }}>
      {children}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ alignItems: "center", display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
      <div style={{ color: C.textTertiary, fontSize: 14, letterSpacing: ".094px", lineHeight: "150%", minWidth: 92 }}>{label}</div>
      {children}
    </div>
  );
}

const pill: React.CSSProperties = {
  background: C.fill, border: "none", borderRadius: 6, color: C.textPrimary, fontSize: 12, fontWeight: 500,
  height: 24, maxWidth: 156, padding: "0 6px",
};

function PillSelect({ value, onChange, disabled, options, testId }: {
  value: string; onChange: (v: string) => void; disabled?: boolean;
  options: Array<{ id: string; label: string }>; testId?: string;
}) {
  return (
    <select value={value} disabled={disabled} data-testid={testId} onChange={(e) => onChange(e.target.value)}
      style={{ ...pill, cursor: disabled ? "default" : "pointer" }}>
      {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
    </select>
  );
}

function FactPill({ children }: { children: React.ReactNode }) {
  return <span style={{ alignItems: "center", color: C.textSecondary, display: "inline-flex", fontSize: 12, fontWeight: 500, height: 24 }}>{children}</span>;
}

const field: React.CSSProperties = {
  background: "rgba(0,0,0,.25)", border: `1px solid ${C.border}`, borderRadius: 6, boxSizing: "border-box",
  color: C.textPrimary, fontSize: 12, padding: "6px 8px", width: "100%",
};

function TextField({ value, onChange, disabled, placeholder, maxLength, testId, rows }: {
  value: string; onChange: (v: string) => void; disabled: boolean; placeholder?: string; maxLength: number; testId: string; rows?: number;
}) {
  if (rows) {
    return (
      <textarea value={value} disabled={disabled} rows={rows} maxLength={maxLength} data-testid={testId} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} style={{ ...field, fontSize: 13, lineHeight: "160%", padding: "8px 10px", resize: "vertical" }} />
    );
  }
  return (
    <input value={value} disabled={disabled} maxLength={maxLength} data-testid={testId} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)} style={field} />
  );
}

function OverlayFields({ overlay, onChange, disabled, prefix, withPrice }: {
  overlay: AdOverlay | null; onChange: (o: AdOverlay | null) => void; disabled: boolean; prefix: string; withPrice: boolean;
}) {
  const { t } = useTranslation();
  if (!overlay) {
    return (
      <button type="button" disabled={disabled} data-testid={`${prefix}-overlay-add`} onClick={() => onChange({ ...EMPTY_OVERLAY })}
        style={{ alignSelf: "flex-start", background: "none", border: `1px dashed ${C.border}`, borderRadius: 6, color: C.muted, cursor: disabled ? "default" : "pointer", fontSize: 12, padding: "4px 10px" }}>
        {t("studio.adBrief.addOverlay")}
      </button>
    );
  }
  const set = (k: keyof AdOverlay) => (v: string) => onChange({ ...overlay, [k]: v });
  return (
    <div style={{ display: "grid", gap: 4 }}>
      <div style={{ color: C.muted, fontSize: 12 }}>{t("studio.adBrief.overlay")}</div>
      <TextField value={overlay.headline} onChange={set("headline")} disabled={disabled} maxLength={60} testId={`${prefix}-headline`} placeholder={t("studio.adBrief.headline")} />
      <TextField value={overlay.subline} onChange={set("subline")} disabled={disabled} maxLength={60} testId={`${prefix}-subline`} placeholder={t("studio.adBrief.subline")} />
      {withPrice && <TextField value={overlay.price} onChange={set("price")} disabled={disabled} maxLength={60} testId={`${prefix}-price`} placeholder={t("studio.adBrief.price")} />}
      <TextField value={overlay.cta} onChange={set("cta")} disabled={disabled} maxLength={60} testId={`${prefix}-cta`} placeholder={t("studio.adBrief.cta")} />
      <button type="button" disabled={disabled} data-testid={`${prefix}-overlay-remove`} onClick={() => onChange(null)}
        style={{ alignSelf: "flex-start", background: "none", border: "none", color: C.muted, cursor: disabled ? "default" : "pointer", fontSize: 12, padding: 0, textDecoration: "underline" }}>
        {t("studio.adBrief.removeOverlay")}
      </button>
    </div>
  );
}

function joinScript(beats: AdBeat[]): string {
  return beats.map((b) => b.narration.trim()).filter(Boolean).join(" ");
}

function nextSeed(plan: AdPlan): number {
  const seeds = [...(plan.beats || []).map((b) => b.seed), ...(plan.stills || []).map((s) => s.seed)];
  const last = seeds.length ? seeds[seeds.length - 1] : 1;
  return (last + 7919) % 2147483647;
}

function reindex<T extends { index: number }>(items: T[]): T[] {
  return items.map((it, i) => ({ ...it, index: i }));
}

export default function AdBriefCard({ brief }: { brief: AdBriefState }) {
  const { t } = useTranslation();
  const [plan, setPlan] = useState<AdPlan>(brief.plan);
  const [inputs, setInputs] = useState<AdInputs>(brief.inputs);
  const [openBeat, setOpenBeat] = useState<number | null>(0);

  // A re-plan replaces the plan wholesale — adopt it, drop stale edits.
  useEffect(() => { setPlan(brief.plan); }, [brief.plan]);
  useEffect(() => { setInputs(brief.inputs); }, [brief.inputs]);

  const locked = !!brief.rendering;
  const offer = plan.offer;
  const hasSpot = offer === "spot" || offer === "package";
  const hasStills = offer === "stills" || offer === "package";
  const beats = plan.beats || [];
  const stills = plan.stills || [];
  const photos = inputs.product.photos;
  const narrationOn = inputs.voice.mode !== "none";
  const duration = plan.duration_s ?? inputs.duration_s;
  const totalSeconds = beats.reduce((n, b) => n + Number(b.seconds || 0), 0);
  const secondsOk = !hasSpot || Math.abs(totalSeconds - duration) <= 0.5;
  const stillsOk = !hasStills || stills.length === 8 || stills.length === 16;

  // The same rules services/ads/contracts.js validatePlan applies, mirrored
  // here so Generate is only ever enabled for a plan the server will accept,
  // and every problem is shown on the beat it belongs to (the server's answer
  // is one sentence with no field to point at).
  const beatProblems: Array<string | null> = beats.map((b) => {
    if (PRODUCT_KINDS.includes(b.kind) && (photos.length === 0 || b.product_photo_index === null || b.product_photo_index >= photos.length)) return "productBeatNeedsPhoto";
    if (AVATAR_KINDS.includes(b.kind) && !inputs.avatar) return "avatarBeatNeedsSelfies";
    if (b.kind !== "text_card" && b.kind !== "end_card" && !b.visual_prompt.trim()) return "promptRequired";
    return null;
  });
  const stillProblems: Array<string | null> = stills.map((s) => {
    if (s.product_photo_index !== null && (photos.length === 0 || s.product_photo_index >= photos.length)) return "productBeatNeedsPhoto";
    if (!s.visual_prompt.trim()) return "stillPromptRequired";
    return null;
  });
  const lastBeat = beats.length ? beats[beats.length - 1] : null;
  const lastIsEndCard = !!lastBeat && lastBeat.kind === "end_card";
  const valid = (!hasSpot || beats.length > 0) && secondsOk && stillsOk
    && beatProblems.every((p) => p === null) && stillProblems.every((p) => p === null);
  const cost = brief.credits;

  // What is sent: the end card only exists when the last beat IS the end
  // card, and it runs for exactly that beat's length (contracts.js: "the last
  // beat must be end_card when end_card is present", "end_card.seconds must
  // match the last beat"). The card's own text stays in state either way, so
  // switching the last beat back restores it.
  const forCommit = useCallback((p: AdPlan): AdPlan => {
    if (!(p.offer === "spot" || p.offer === "package")) return p;
    const list = p.beats || [];
    const last = list.length ? list[list.length - 1] : null;
    if (!last || last.kind !== "end_card") return { ...p, end_card: null };
    return { ...p, end_card: { headline: "", subline: "", cta: "", show_logo: true, ...(p.end_card || {}), seconds: last.seconds } };
  }, []);

  // Chat quick-actions merge into the LIVE state so a chip can never wipe a
  // prompt the user already rewrote.
  useAdPlanPatch(useCallback((patch: Partial<AdPlan>) => {
    if (locked) return;
    setPlan((p) => ({ ...p, ...patch }));
  }, [locked]));
  useAdInputsPatch(useCallback((patch: Partial<AdInputs>) => {
    if (locked) return;
    setInputs((p) => ({ ...p, ...patch }));
  }, [locked]));
  // The pay sheet keeps exactly what is on the card through checkout.
  usePayJob("ads", () => ({ payload: { plan: forCommit(plan), inputs } }));
  // "Start generation" from chat commits exactly what is on the card now.
  useAdCommit(useCallback(() => {
    if (locked || !valid) return;
    requestAdRender(forCommit(plan), inputs);
  }, [locked, valid, plan, inputs, forCommit]));

  const patchBeat = (i: number, next: Partial<AdBeat>) => setPlan((p) => {
    const nb = (p.beats || []).map((b, n) => (n === i ? { ...b, ...next } : b));
    const scriptTouched = "narration" in next;
    return { ...p, beats: nb, script: scriptTouched ? joinScript(nb) : p.script };
  });
  const setKind = (i: number, kind: AdBeatKind) => {
    const isProduct = PRODUCT_KINDS.includes(kind);
    patchBeat(i, {
      kind,
      product_photo_index: isProduct ? (beats[i].product_photo_index ?? (photos.length ? 0 : null)) : null,
    });
  };
  const removeBeat = (i: number) => setPlan((p) => {
    const nb = reindex((p.beats || []).filter((_, n) => n !== i));
    return { ...p, beats: nb, script: joinScript(nb) };
  });
  const addBeat = () => setPlan((p) => {
    const cur = p.beats || [];
    const fresh: AdBeat = {
      index: 0, kind: "lifestyle", seconds: 2.5, visual_prompt: "", motion: "slow push in",
      product_photo_index: null, placement: "center", narration: "", overlay: null, seed: nextSeed(p),
    };
    const endIdx = cur.length && cur[cur.length - 1].kind === "end_card" ? cur.length - 1 : cur.length;
    const nb = reindex([...cur.slice(0, endIdx), fresh, ...cur.slice(endIdx)]);
    setOpenBeat(endIdx);
    return { ...p, beats: nb };
  });
  const patchStill = (i: number, next: Partial<AdStill>) => setPlan((p) => ({ ...p, stills: (p.stills || []).map((s, n) => (n === i ? { ...s, ...next } : s)) }));
  const patchEnd = (next: Partial<NonNullable<AdPlan["end_card"]>>) => setPlan((p) => ({
    ...p,
    end_card: { headline: "", subline: "", cta: "", show_logo: true, seconds: 2.5, ...(p.end_card || {}), ...next },
  }));

  const voiceOptions = [
    { id: "none", label: t("studio.adBrief.voiceNone") },
    ...(inputs.voice.voice_id || inputs.voice.mode === "stock" ? [{ id: "stock", label: t("studio.adBrief.voiceStock") }] : []),
    ...(inputs.voice.sample_url || inputs.voice.saved_voice_id ? [{ id: "clone", label: t("studio.adBrief.voiceClone") }] : []),
  ];
  const musicOptions = [
    { id: "none", label: t("studio.adBrief.musicNone") },
    ...(inputs.music.url ? [{ id: "upload", label: t("studio.adBrief.musicUpload") }] : []),
    ...(inputs.music.track_id ? [{ id: "stock", label: t("studio.adBrief.musicStock") }] : []),
  ];
  const photoOptions = photos.map((_, i) => ({ id: String(i), label: t("studio.adBrief.photoN", { n: i + 1 }) }));

  const btnDisabled = locked || !valid;

  return (
    <div data-testid="ad-brief-card" className="nodrag nowheel" style={{
      boxSizing: "border-box", display: "flex", flexDirection: "column", gap: 16, minHeight: 376, padding: 20,
      position: "relative", width: 376, background: C.cardBg, color: C.textPrimary, fontFamily: "system-ui, sans-serif",
    }}>
      {/* ---- Summary ---- */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <SectionTitle>{t("studio.adBrief.summary")}</SectionTitle>
        <div style={{ fontSize: 12, lineHeight: 1.6, color: C.textTertiary }}>
          {t("studio.adBrief.planLead")} <b style={{ color: C.textPrimary }}>{t("studio.adBrief.planBold")}</b> {t("studio.adBrief.planTail")}
        </div>
        <Row label={t("studio.adBrief.offer")}><FactPill>{t(`studio.adBrief.offers.${offer}`)}</FactPill></Row>
        {hasSpot && (
          <Row label={t("studio.adBrief.format")}>
            <FactPill>{t("studio.adBrief.formatValue", { seconds: duration, aspect: plan.aspect ?? inputs.aspect })}</FactPill>
          </Row>
        )}
        {hasStills && (
          <Row label={t("studio.adBrief.stills")}>
            <FactPill>{t("studio.adBrief.stillsValue", { count: stills.length })}</FactPill>
          </Row>
        )}
        <Row label={t("studio.adBrief.product")}>
          <FactPill>
            <span data-testid="ad-product-lock">{photos.length ? t("studio.adBrief.productLocked", { count: photos.length }) : t("studio.adBrief.noProductPhoto")}</span>
          </FactPill>
        </Row>
        <Row label={t("studio.adBrief.credits")}>
          <FactPill><span data-testid="ad-credits" style={{ color: C.gold, fontWeight: 700 }}>{cost === null ? "—" : t("studio.adBrief.creditsValue", { count: cost })}</span>
            {typeof brief.estimatedMinutes === "number" && <span style={{ color: C.muted, marginLeft: 6 }}>{t("studio.adBrief.estimatedMinutes", { minutes: brief.estimatedMinutes })}</span>}
          </FactPill>
        </Row>
        {brief.planner === "fallback" && <div style={{ color: C.muted, fontSize: 12 }}>{t("studio.adBrief.fallbackPlanner")}</div>}
      </div>

      {/* ---- Switches the worker consumes ---- */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <SectionTitle>{t("studio.adBrief.keyElements")}</SectionTitle>
        <Row label={t("studio.adBrief.voice")}>
          <PillSelect testId="ad-voice-mode" value={inputs.voice.mode} disabled={locked} options={voiceOptions}
            onChange={(v) => setInputs((p) => ({ ...p, voice: { ...p.voice, mode: v as AdInputs["voice"]["mode"] } }))} />
        </Row>
        {inputs.avatar && narrationOn && (
          <Row label={t("studio.adBrief.avatarSpeaks")}>
            <label style={{ alignItems: "center", display: "inline-flex", gap: 6, fontSize: 12, color: C.textSecondary }}>
              <input type="checkbox" data-testid="ad-avatar-speaks" checked={inputs.voice.avatar_speaks} disabled={locked}
                onChange={(e) => setInputs((p) => ({ ...p, voice: { ...p.voice, avatar_speaks: e.target.checked } }))} />
              {t("studio.adBrief.avatarSpeaksValue")}
            </label>
          </Row>
        )}
        <Row label={t("studio.adBrief.music")}>
          <PillSelect testId="ad-music-mode" value={inputs.music.mode} disabled={locked} options={musicOptions}
            onChange={(v) => setInputs((p) => ({ ...p, music: { ...p.music, mode: v as AdInputs["music"]["mode"] } }))} />
        </Row>
        <Row label={t("studio.adBrief.captions")}>
          <label style={{ alignItems: "center", display: "inline-flex", gap: 6, fontSize: 12, color: C.textSecondary }}>
            <input type="checkbox" data-testid="ad-captions" checked={inputs.captions} disabled={locked}
              onChange={(e) => setInputs((p) => ({ ...p, captions: e.target.checked }))} />
            {narrationOn ? t("studio.adBrief.captionsValue") : t("studio.adBrief.captionsNeedVoice")}
          </label>
        </Row>
      </div>

      {/* ---- Beats ---- */}
      {hasSpot && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <SectionTitle>{t("studio.adBrief.beats")}</SectionTitle>
          <div data-testid="ad-seconds-total" style={{ color: secondsOk ? C.muted : C.red, fontSize: 12 }}>
            {t("studio.adBrief.totalSeconds", { total: totalSeconds, target: duration })}
            {!secondsOk && <> — {t("studio.adBrief.secondsMismatch")}</>}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {beats.map((beat, i) => {
              const open = openBeat === i;
              const isProduct = PRODUCT_KINDS.includes(beat.kind);
              const p = `ad-beat-${i}`;
              return (
                <div key={i} data-testid={p} style={{ background: C.fill, border: `1px solid ${open ? C.green : "transparent"}`, borderRadius: 8, padding: 8 }}>
                  <div style={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
                    <span style={{ color: C.muted, fontSize: 12, fontWeight: 700 }}>{String(i + 1).padStart(2, "0")}</span>
                    <PillSelect testId={`${p}-kind`} value={beat.kind} disabled={locked}
                      options={KINDS.map((k) => ({ id: k, label: t(`studio.adBrief.kinds.${k}`) }))} onChange={(v) => setKind(i, v as AdBeatKind)} />
                    <PillSelect testId={`${p}-seconds`} value={String(beat.seconds)} disabled={locked}
                      options={SECONDS.map((s) => ({ id: String(s), label: t("studio.adBrief.secondsValue", { seconds: s }) }))}
                      onChange={(v) => patchBeat(i, { seconds: Number(v) as AdBeatSeconds })} />
                    <button type="button" onClick={() => setOpenBeat(open ? null : i)} data-testid={`${p}-toggle`}
                      style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 13, marginLeft: "auto" }}>
                      {open ? t("studio.adBrief.hide") : t("studio.adBrief.edit")}
                    </button>
                    <button type="button" disabled={locked} onClick={() => removeBeat(i)} data-testid={`${p}-remove`}
                      style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 5, color: C.muted, cursor: locked ? "default" : "pointer", fontSize: 12, padding: "1px 6px" }}>
                      ✕
                    </button>
                  </div>
                  {beatProblems[i] && (
                    <div data-testid={`${p}-problem`} style={{ color: C.red, fontSize: 12, marginBottom: 6 }}>{t(`studio.adBrief.${beatProblems[i]}`)}</div>
                  )}
                  <TextField value={beat.visual_prompt} onChange={(v) => patchBeat(i, { visual_prompt: v })} disabled={locked} maxLength={400}
                    testId={`${p}-prompt`} placeholder={t("studio.adBrief.visualPromptPlaceholder")} rows={2} />
                  {open && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 6 }}>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        <PillSelect testId={`${p}-motion`} value={beat.motion} disabled={locked}
                          options={MOTIONS.map((m) => ({ id: m, label: t(`studio.adBrief.motions.${motionKey(m)}`) }))}
                          onChange={(v) => patchBeat(i, { motion: v as AdMotion })} />
                        {isProduct && (
                          <>
                            <PillSelect testId={`${p}-photo`} value={String(beat.product_photo_index ?? 0)} disabled={locked || !photos.length}
                              options={photoOptions.length ? photoOptions : [{ id: "0", label: t("studio.adBrief.noProductPhoto") }]}
                              onChange={(v) => patchBeat(i, { product_photo_index: Number(v) })} />
                            <PillSelect testId={`${p}-placement`} value={beat.placement} disabled={locked}
                              options={PLACEMENTS.map((pl) => ({ id: pl, label: t(`studio.adBrief.placements.${pl}`) }))}
                              onChange={(v) => patchBeat(i, { placement: v as AdPlacement })} />
                          </>
                        )}
                      </div>
                      {narrationOn && (
                        <div>
                          <div style={{ color: C.muted, fontSize: 12, marginBottom: 2 }}>{t("studio.adBrief.narration")}</div>
                          <TextField value={beat.narration} onChange={(v) => patchBeat(i, { narration: v })} disabled={locked} maxLength={240}
                            testId={`${p}-narration`} placeholder={t("studio.adBrief.narrationPlaceholder")} rows={2} />
                        </div>
                      )}
                      <OverlayFields overlay={beat.overlay} onChange={(o) => patchBeat(i, { overlay: o })} disabled={locked} prefix={p} withPrice />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {!locked && (
            <button type="button" onClick={addBeat} data-testid="ad-beat-add"
              style={{ alignSelf: "flex-start", background: "none", border: `1px dashed ${C.border}`, borderRadius: 8, color: C.muted, cursor: "pointer", fontSize: 12, padding: "6px 12px" }}>
              {t("studio.adBrief.addBeat")}
            </button>
          )}
        </div>
      )}

      {/* ---- Script ---- */}
      {hasSpot && narrationOn && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <SectionTitle>{t("studio.adBrief.script")}</SectionTitle>
          <div style={{ color: C.muted, fontSize: 12 }}>{t("studio.adBrief.scriptNote")}</div>
          <TextField value={plan.script || ""} onChange={(v) => setPlan((p) => ({ ...p, script: v }))} disabled={locked} maxLength={24 * 240}
            testId="ad-script" placeholder={t("studio.adBrief.scriptPlaceholder")} rows={3} />
        </div>
      )}

      {/* ---- End card ---- */}
      {hasSpot && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <SectionTitle>{t("studio.adBrief.endCard")}</SectionTitle>
          <div data-testid="ad-endcard-hint" style={{ color: lastIsEndCard ? C.muted : C.red, fontSize: 12 }}>
            {lastIsEndCard ? t("studio.adBrief.endCardSeconds", { seconds: lastBeat?.seconds }) : t("studio.adBrief.endCardNeedsLastBeat")}
          </div>
          <div style={{ display: "grid", gap: 4 }}>
            <TextField value={plan.end_card?.headline || ""} onChange={(v) => patchEnd({ headline: v })} disabled={locked} maxLength={60} testId="ad-endcard-headline" placeholder={t("studio.adBrief.headline")} />
            <TextField value={plan.end_card?.subline || ""} onChange={(v) => patchEnd({ subline: v })} disabled={locked} maxLength={60} testId="ad-endcard-subline" placeholder={t("studio.adBrief.subline")} />
            <TextField value={plan.end_card?.cta || ""} onChange={(v) => patchEnd({ cta: v })} disabled={locked} maxLength={60} testId="ad-endcard-cta" placeholder={t("studio.adBrief.cta")} />
            <label style={{ alignItems: "center", display: "inline-flex", gap: 6, fontSize: 12, color: C.textSecondary }}>
              <input type="checkbox" data-testid="ad-endcard-logo" checked={plan.end_card?.show_logo ?? true} disabled={locked}
                onChange={(e) => patchEnd({ show_logo: e.target.checked })} />
              {inputs.brand.logo_url ? t("studio.adBrief.showLogo") : t("studio.adBrief.showLogoNoLogo")}
            </label>
          </div>
        </div>
      )}

      {/* ---- Still concepts ---- */}
      {hasStills && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <SectionTitle>{t("studio.adBrief.stillConcepts")}</SectionTitle>
          {!stillsOk && <div style={{ color: C.red, fontSize: 12 }}>{t("studio.adBrief.stillsCountRule")}</div>}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {stills.map((still, i) => {
              const p = `ad-still-${i}`;
              const open = openBeat === 1000 + i;
              return (
                <div key={i} data-testid={p} style={{ background: C.fill, border: `1px solid ${open ? C.green : "transparent"}`, borderRadius: 8, padding: 8 }}>
                  <div style={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
                    <span style={{ color: C.muted, fontSize: 12, fontWeight: 700 }}>{String(i + 1).padStart(2, "0")}</span>
                    <PillSelect testId={`${p}-aspect`} value={still.aspect} disabled={locked}
                      options={STILL_ASPECTS.map((a) => ({ id: a, label: a }))} onChange={(v) => patchStill(i, { aspect: v as AdStillAspect })} />
                    <PillSelect testId={`${p}-photo`} value={still.product_photo_index === null ? "none" : String(still.product_photo_index)} disabled={locked}
                      options={[{ id: "none", label: t("studio.adBrief.noProduct") }, ...photoOptions]}
                      onChange={(v) => patchStill(i, { product_photo_index: v === "none" ? null : Number(v) })} />
                    <button type="button" onClick={() => setOpenBeat(open ? null : 1000 + i)} data-testid={`${p}-toggle`}
                      style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 13, marginLeft: "auto" }}>
                      {open ? t("studio.adBrief.hide") : t("studio.adBrief.edit")}
                    </button>
                  </div>
                  {stillProblems[i] && (
                    <div data-testid={`${p}-problem`} style={{ color: C.red, fontSize: 12, marginBottom: 6 }}>{t(`studio.adBrief.${stillProblems[i]}`)}</div>
                  )}
                  <TextField value={still.visual_prompt} onChange={(v) => patchStill(i, { visual_prompt: v })} disabled={locked} maxLength={400}
                    testId={`${p}-prompt`} placeholder={t("studio.adBrief.visualPromptPlaceholder")} rows={2} />
                  {open && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 6 }}>
                      {still.product_photo_index !== null && (
                        <PillSelect testId={`${p}-placement`} value={still.placement} disabled={locked}
                          options={PLACEMENTS.map((pl) => ({ id: pl, label: t(`studio.adBrief.placements.${pl}`) }))}
                          onChange={(v) => patchStill(i, { placement: v as AdPlacement })} />
                      )}
                      <OverlayFields overlay={still.overlay} onChange={(o) => patchStill(i, { overlay: o })} disabled={locked} prefix={p} withPrice />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ---- Commit ---- */}
      <SiteMarkSwitch testId="ad-site-mark" disabled={locked} style={{ color: C.textSecondary, marginTop: "auto" }} />
      <div style={{ alignItems: "center", borderTop: `1px solid ${C.border}`, display: "flex", flexWrap: "wrap", gap: 8, paddingTop: 12 }}>
        <button type="button" data-testid="ad-generate" disabled={btnDisabled}
          onClick={() => requestAdRender(forCommit(plan), inputs)}
          style={{
            background: btnDisabled ? "rgba(255,255,255,.08)" : `linear-gradient(135deg,${C.green},#15803d)`,
            border: "none", borderRadius: 10, color: btnDisabled ? C.muted : "#fff",
            cursor: btnDisabled ? "default" : "pointer", fontSize: 13, fontWeight: 700, letterSpacing: .3, padding: "10px 16px",
          }}>
          {locked ? t("studio.adBrief.rendering") : t("studio.adBrief.generate")}
          {cost !== null && !locked ? t("studio.adBrief.costSuffix", { cost: cost.toLocaleString() }) : ""}
        </button>
        <button type="button" disabled={locked} onClick={() => requestAdReplan()} data-testid="ad-replan"
          style={{ background: C.fill, border: "none", borderRadius: 8, color: C.textSecondary, cursor: locked ? "default" : "pointer", fontSize: 12, padding: "9px 12px" }}>
          {t("studio.adBrief.planAgain")}
        </button>
        <button type="button" disabled={locked} onClick={() => requestAdDiscard()} data-testid="ad-discard"
          style={{ background: "none", border: "none", color: C.muted, cursor: locked ? "default" : "pointer", fontSize: 12, textDecoration: "underline" }}>
          {t("studio.adBrief.discard")}
        </button>
        {locked && <span style={{ color: C.muted, fontSize: 12 }}>{t("studio.adBrief.lockedNote")}</span>}
      </div>
    </div>
  );
}
